import React, { useEffect, useState, useCallback, useContext } from 'react'
import ChartTypes from './components/ChartTypes'
import './ICMCharts.scss'
import { getChart, getPreflopData } from './api'
import { Actions } from './components/Actions/Actions'
import { ChartView } from './components/ChartView/ChartView'
import { ChartHeader } from './components/ChartHeader/ChartHeader'
import NumberOfHands from './components/NumberOfHands'
import { NUMBER_OF_HANDS } from './mock'
import { useLocation } from 'react-router-dom'
import FilterToggle from '../PreflopCharts/components/FilterToggle'
import { AppContext } from '../../AppContext'

const PageTitle = ({ isFiltersExpanded, toggleFilters }) => {
  return (
    <div className="ICMCharts__title-container">
      <h2
        className="ICMCharts__title"
      >
        ICM Chart
      </h2>
      <FilterToggle
        isFiltersExpanded={isFiltersExpanded}
        toggleFilters={toggleFilters}
      />
    </div>
  )
}

export const ICMCharts = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    type: null,
    chartType: '3 WAY',
    action: null,
  })
  const chartTypeValue = NUMBER_OF_HANDS[selectedFilters.chartType]
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true)
  const [filters, setFilters] = useState({
    actions: [],
  })
  const [chartsData, setChartsData] = useState(null)
  const [type, setType] = useState(null)
  const [config, setConfig] = useState({
    colors: null,
    blindsRestrictions: null,
    huRestrictions: null,
    icmRestrictions: null,
  })
  const { colors } = config;
  const [isInitialLoaded, setInitialLoaded] = useState(false)
  const { user } = useContext(AppContext);

  const showAccessPopup = (requiredAccess) => {
    window.showPopupPlan({
      title: "Start your PokerCoaching membership today to unlock this content!",
      planSet:
        requiredAccess === "pc"
          ? "standard-premium"
          : requiredAccess === "pcp"
          ? "premium"
          : "free",
      userAccess: user.type,
    });
  }

  const { search } = useLocation()


  useEffect(() => {
    if (isInitialLoaded) return

    const typeParam = search.split('?type=')[1]
    let type = null
    if (typeParam) {
      type = typeParam.split('&')[0]
    }
    if (!type) {
      setType('online')
      setInitialLoaded(true)
    }
    let typeValue = 'online'
    switch (type) {
      case 'online':
        typeValue = 'online'
        break
      case 'live':
        typeValue = 'live'
        break
      default:
        break
    }
    setType(typeValue)

    setInitialLoaded(true)
  }, [search, isInitialLoaded])

  const updateActions = useCallback(
    (actions) => {
      setFilters({ ...filters, actions })
    },
    [setFilters, filters],
  )

  const getInitialData = useCallback(async () => {
    const data = await getChart({
      chartType: chartTypeValue,
      action: 'icm_chart_init',
      type,
    })
    const { init_filter: actions } = data
    updateActions(actions)
  }, [chartTypeValue, updateActions, type])

  const getChartColors = useCallback(async () => {
    const data = await getPreflopData()
    const { color_pallet:colors, access_hu_restrict: huRestrictions, access_icm_restrict:icmRestrictions , access_blind_restrict: blindsRestrictions } = data?.settings
    setConfig({colors, huRestrictions, icmRestrictions, blindsRestrictions})
  }, [setConfig])

  useEffect(() => {
    if (!type || !chartTypeValue) return
    getChartColors()
    getInitialData()
    setChartsData(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTypeValue, type])

  const getFolderHandler = async (folder) => {
    const data = await getChart({
      chartType: chartTypeValue,
      action: 'icm_chart_handler',
      folder,
      type,
    })
    const { structure } = data
    generateCharts(structure, folder)
  }

  const getPosition = (query) => {
    if (!query) return chartTypeValue === '5way' ? 'UTG' : 'BTN'
    const queryArray = query.split('/')

    const positionsOrder =
      chartTypeValue === '5way'
        ? ['UTG', 'CO', 'BTN', 'SB', 'BB']
        : ['BTN', 'SB', 'BB']

    let position
    queryArray.forEach((actionLabel, idx) => {
      const action = actionLabel.toLowerCase().trim()
      position = positionsOrder[idx + 1]
      if (action !== 'fold') positionsOrder.push(positionsOrder[idx])
    })

    return position
  }

  const generateCharts = async (structure, rootFolder, path = false) => {
    const queryArray = []

    const actionArray = structure.length ? structure : structure.children

    const position = getPosition(path)
    const { id_path: idPath } = structure
    queryArray.push({
      action: 'icm_chart_get',
      chartType: chartTypeValue,
      folder: rootFolder,
      idPath: idPath?.length ? idPath : '',
      children: actionArray,
      position,
      type,
    })

    const getFoldChart = (foldStructure) => {
      const actionArray = foldStructure.length
        ? foldStructure
        : foldStructure.children
      const foldChart = actionArray.find((item) => item.name === 'FOLD')
      if (foldChart) {
        const position = getPosition(foldChart.path)
        const { id_path: idPath, root_folder: folder, children } = foldChart
        queryArray.push({
          action: 'icm_chart_get',
          chartType: chartTypeValue,
          folder,
          idPath,
          children,
          position,
          type,
        })

        getFoldChart(foldChart)
      }
    }
    getFoldChart(structure)

    setChartsData(queryArray)
  }

  const updateSelectedFilters = ({ key, value }) => {
    setSelectedFilters({ ...selectedFilters, [key]: value })
  }
  const rewriteSelectedFiltersAction = ({ key, value }) => {
    const { type, chartType, action } = selectedFilters
    setSelectedFilters({ type, chartType, action, [key]: value })
  }

  const sendChartOptionSelectToAnalytics = (value, type) => {
    // actid=32
    window.analytics?.track("chart_option:click", {
      "type": type,
      "value": value
    });
  }

  return (
    <div
      style={{ minWidth: '100vw', minHeight: '100vh' }}
      className="ICMCharts"
    >
      <PageTitle
        isFiltersExpanded={isFiltersExpanded}
        toggleFilters={() => setIsFiltersExpanded(!isFiltersExpanded)}
      />

      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div
          className={`ICMCharts__chart-filters ${
            isFiltersExpanded ? 'expanded' : ''
          } ${chartTypeValue === '5way' ? 'five-way' : ''}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginRight: '40px',
            flexGrow: 1,
            rowGap: '15px',
          }}
        >
          <ChartTypes
            setType={setType}
            type={type}
            rewriteSelectedFiltersAction={rewriteSelectedFiltersAction}
          />
          <NumberOfHands
            selectedFilters={selectedFilters}
            rewriteSelectedFiltersAction={rewriteSelectedFiltersAction}
            sendChartOptionSelectToAnalytics={(value) => sendChartOptionSelectToAnalytics(value, 'Number of Hands')}
          />
          <Actions
            getFolderHandler={getFolderHandler}
            actions={filters.actions}
            selectedFilters={selectedFilters}
            rewriteSelectedFiltersAction={rewriteSelectedFiltersAction}
            config={config}
            userAccess={user.type}
            type={type}
            showAccessPopup={showAccessPopup}
            sendChartOptionSelectToAnalytics={(value) => sendChartOptionSelectToAnalytics(value, 'Action')}
          />
        </div>
        <div>
          <ChartHeader
            rewriteSelectedFiltersAction={rewriteSelectedFiltersAction}
            selectedFilters={selectedFilters}
          />

          <ChartView
            type={chartTypeValue}
            colors={colors}
            generateCharts={generateCharts}
            chartsData={chartsData}
            updateSelectedFilters={updateSelectedFilters}
          />
        </div>
      </div>
    </div>
  )
}
